import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, useTheme, useMediaQuery } from '@mui/material';

import { colors, spacing, radius } from 'common/theme';
import { ButtonHot, BUTTON_MODE } from 'components/common/ButtonHot';
import { IconHot } from 'components/common/IconHot';
import { LogoutModal } from 'components/common/LogoutModal';
import { logout } from 'redux/actions';
import * as routes from 'constants/routes';
import { generateTestId } from 'app/functions';
import AnalyticsManager from 'app/analytics';
import { AnalyticsParamValues } from 'constants/analytics';

import { USER_INFO_MENU_IDS, useUserInfoMenuConfig } from '../utils/enums';

const PersonalZoneNavbar = ({ testID = 'personal-zone-navbar' }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const activeTabRef = useRef(null);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuConfig = useUserInfoMenuConfig();

  const activeItem = menuConfig.find((item) => 
    location.pathname.includes(item.path)
  )?.id || USER_INFO_MENU_IDS.COUPONS;

  useEffect(() => {
    if (activeTabRef.current) {
      activeTabRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }, [activeItem]);

  useEffect(() => {
    if (!isMobile) return;

    const checkMobileMenuOpen = () => {
      const mobileMenuWrapper = document.querySelector('.mobile-menu-wrapper');
      const isOpen = mobileMenuWrapper?.classList.contains('open') || false;
      setIsMobileMenuOpen(isOpen);
    };

    checkMobileMenuOpen();

    const observer = new MutationObserver(checkMobileMenuOpen);
    const mobileMenuWrapper = document.querySelector('.mobile-menu-wrapper');
    
    if (mobileMenuWrapper) {
      observer.observe(mobileMenuWrapper, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [isMobile]);

  const handleMenuClick = (menuItem) => {
    if (menuItem.id === USER_INFO_MENU_IDS.LOGOUT) {
      setIsLogoutModalOpen(true);
    } else {
      let analyticsValue;
      switch (menuItem.id) {
        case USER_INFO_MENU_IDS.COUPONS:
        case USER_INFO_MENU_IDS.ORDER_HISTORY:
          analyticsValue = AnalyticsParamValues.MY_ORDERS;
          break;
        case USER_INFO_MENU_IDS.PERSONAL_INFO:
          analyticsValue = AnalyticsParamValues.PERSONAL_DETAILS;
          break;
        case USER_INFO_MENU_IDS.FAVORITES:
          analyticsValue = AnalyticsParamValues.ADDRESS;
          break;
        case USER_INFO_MENU_IDS.SAVINGS:
          analyticsValue = AnalyticsParamValues.PAYMENT_METHODS;
          break;
        default:
          analyticsValue = AnalyticsParamValues.PERSONAL_DETAILS;
      }
      AnalyticsManager.logPersonalZoneMenuItemPress(analyticsValue);
      history.push(`${routes.user_info}${menuItem.path}`);
    }
  };

  const handleLogoutConfirm = () => {
    AnalyticsManager.logPersonalZoneMenuItemPress(AnalyticsParamValues.LOGOUT);
    AnalyticsManager.logLogout();
    dispatch(logout());
    setIsLogoutModalOpen(false);
    history.push(routes.home);
  };

  if (isMobile) {
    return (
      <>
        <Box
          sx={{
            position: 'sticky',
            top: '70px', 
            zIndex: 50,
            width: '100%',
            backgroundColor: colors.system.surface.subtle,
            borderBottom: `1px solid ${colors.system.border.subtle}`,
            padding: `${spacing.sm}px`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              overflowX: 'auto',
              scrollBehavior: 'smooth',
              gap: `${spacing.xs}px`,
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
          >
            {menuConfig.map((item) => {
              const isActive = activeItem === item.id;
              return (
                <Box 
                  key={item.id} 
                  ref={isActive ? activeTabRef : null}
                  sx={{ 
                    flexShrink: 0,
                    transition: 'all 0.3s ease',
                    width: `calc(100% / ${menuConfig.length})`,
                    minWidth: 'fit-content',
                  }}
                >
                  <ButtonHot
                    testID={generateTestId(testID, `btn-${item.id}`)}
                    mode={BUTTON_MODE.TAB}
                    isActive={isActive}
                    startIcon={
                      <Box
                        sx={{
                          filter: isActive ? 'none' : 'grayscale(100%) opacity(0.5)',
                          transition: 'filter 0.3s ease',
                        }}
                      >
                        <IconHot src={item.icon} size={30} />
                      </Box>
                    }
                    onClick={() => handleMenuClick(item)}
                    sx={{
                      backgroundColor: isActive ? colors.palette.blue400 : 'transparent',
                      color: isActive ? colors.palette.blue800 : colors.system.text.secondary,
                      opacity: isActive ? 1 : 0.6,
                      borderRadius: `${radius.md}px`,
                      borderBottom: 'none',
                      boxShadow: isActive ? '0px 2px 8px rgba(0, 0, 0, 0.08)' : 'none',
                      padding: `${spacing.md}px ${spacing.lg}px`,
                      minWidth: 'auto',
                      fontWeight: isActive ? 'bold' : 'regular',
                      '&:hover': {
                        backgroundColor: isActive ? colors.palette.blue400 : colors.palette.blue300,
                        boxShadow: isActive ? '0px 2px 8px rgba(0, 0, 0, 0.08)' : 'none',
                        opacity: 1,
                      },
                    }}
                  >
                    {item.label}
                  </ButtonHot>
                </Box>
              );
            })}
          </Box>
        </Box>

        <LogoutModal
          open={isLogoutModalOpen}
          onClose={() => setIsLogoutModalOpen(false)}
          onConfirm={handleLogoutConfirm}
          testID={generateTestId(testID, 'logout-modal')}
        />
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          width: { md: 200, lg: 260 },
          flexShrink: 0,
          flexDirection: 'column',
          gap: `${spacing.xxl}px`,
          padding: { md: `${spacing.sm}px`, lg: `${spacing.md}px` },
          borderRadius: `${radius.xxl}px`,
          backgroundColor: colors.system.surface.subtle,
          alignSelf: 'flex-start',
        }}
      >
        <Box sx={{ borderRadius: `${radius.lg}px`, overflow: 'hidden' }}>
          {menuConfig.map((item, index) => (
            <ButtonHot
              key={item.id}
              testID={generateTestId(testID, `btn-${item.id}`)}
              mode={BUTTON_MODE.MENU}
              isActive={activeItem === item.id}
              startIcon={<IconHot src={item.icon} size={30} />}
              onClick={() => handleMenuClick(item)}
              isLast={index === menuConfig.length - 1}
            >
              {item.label}
            </ButtonHot>
          ))}
        </Box>
      </Box>

      <LogoutModal
        open={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={handleLogoutConfirm}
        testID={generateTestId(testID, 'logout-modal')}
      />
    </>
  );
};

export default PersonalZoneNavbar;